import { drawImage } from 'canvas-object-fit';
import { memo, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ClipType } from '@air/api/types';
import { setUploadImageLoadingAction, setUploadImagePreviewAction, type Upload } from '@air/redux-uploader';

import { isPreviewAvailable } from '../utils';

export const UPLOAD_PREVIEW_SIZE = 400;

type UploadImageLoaderProps = {
  upload: Upload;
};

export const UploadPreviewLoader = memo(
  ({
    upload,
    upload: {
      apiUploadData: { type },
    },
  }: UploadImageLoaderProps) => {
    const dispatch = useDispatch();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const loadVideoPreview = useCallback(() => {
      const video = document.createElement('video');
      video.preload = 'auto';
      const source = document.createElement('source');
      video.appendChild(source);
      const src = URL.createObjectURL(upload.file);
      source.src = src;
      source.type = `video/${upload.apiUploadData.ext}`;

      video.addEventListener('loadeddata', () => {
        if (canvasRef.current) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');

          if (ctx) {
            const videoRatio = video.videoWidth / video.videoHeight;
            canvas.height = UPLOAD_PREVIEW_SIZE;
            canvas.width = UPLOAD_PREVIEW_SIZE * videoRatio;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const url = canvas.toDataURL();
            dispatch(setUploadImagePreviewAction({ imageBase64: url, uploadId: upload.id }));
            URL.revokeObjectURL(src);
          }
        }
      });
      video.load();
    }, [dispatch, upload.apiUploadData.ext, upload.file, upload.id]);

    // this has better performance than using getBase64
    const loadImagePreview = useCallback(() => {
      const image = document.createElement('img');
      const src = URL.createObjectURL(upload.file);

      image.onload = () => {
        if (canvasRef.current) {
          const canvas = canvasRef.current;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = UPLOAD_PREVIEW_SIZE;
            canvas.height = UPLOAD_PREVIEW_SIZE;

            drawImage(ctx, image, 0, 0, UPLOAD_PREVIEW_SIZE, UPLOAD_PREVIEW_SIZE, { objectFit: 'cover' });
            dispatch(setUploadImagePreviewAction({ imageBase64: canvas.toDataURL(), uploadId: upload.id }));
            URL.revokeObjectURL(src);
          }
        }
      };
      image.src = src;
    }, [dispatch, upload.file, upload.id]);

    useEffect(() => {
      if (isPreviewAvailable(upload) && !upload.imageBase64 && !upload.isImageLoading) {
        dispatch(setUploadImageLoadingAction({ isImageLoading: true, uploadId: upload.id }));
        if (type === ClipType.video) {
          loadVideoPreview();
        } else {
          loadImagePreview();
        }
      }
    }, [dispatch, loadImagePreview, loadVideoPreview, type, upload, upload.imageBase64]);

    if (upload.imageBase64 || !isPreviewAvailable(upload)) {
      return null;
    }

    return (
      <div className="hidden overflow-hidden" style={{ height: UPLOAD_PREVIEW_SIZE, width: UPLOAD_PREVIEW_SIZE }}>
        <canvas ref={canvasRef} />
      </div>
    );
  },
);

UploadPreviewLoader.displayName = 'UploadPreviewLoader';
